import React, { useCallback, useEffect, useRef, useState } from "react";
import { MAX_RATIO, MIN_RATIO, useSplitView, useWideEnoughForSplit } from "../../lib/splitView";
import SecondaryPane from "./SecondaryPane";

/**
 * Lays the main pane and the split pane side by side with a draggable divider.
 *
 * When the split is off — or the window is too narrow for it — this renders
 * its children and nothing else, so the single-pane app keeps exactly the DOM
 * and the page-level scrolling it has always had. The two-pane layout is the
 * only mode that switches to per-pane scrolling, which is the point: a long
 * blotter on the left must not push the recon on the right off the screen.
 */
const SplitWorkspace: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { open, ratio, setRatio } = useSplitView();
  const wide = useWideEnoughForSplit();
  const active = open && wide;

  const containerRef = useRef<HTMLDivElement>(null);
  // While dragging, the ratio is local state: committing every pointermove to
  // the provider would write localStorage ~60 times a second.
  const [dragRatio, setDragRatio] = useState<number | null>(null);
  const shown = dragRatio ?? ratio;

  const clamp = (r: number) => Math.min(MAX_RATIO, Math.max(MIN_RATIO, r));

  const ratioFromClientX = useCallback((clientX: number): number | null => {
    const el = containerRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return null;
    return clamp((clientX - rect.left) / rect.width);
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragRatio(ratio);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragRatio === null) return;
    const next = ratioFromClientX(e.clientX);
    if (next !== null) setDragRatio(next);
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragRatio === null) return;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    setRatio(dragRatio);
    setDragRatio(null);
  };

  // A drag that crosses a table or a chart would otherwise select text in it.
  useEffect(() => {
    if (dragRatio === null) return;
    const prev = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    return () => { document.body.style.userSelect = prev; };
  }, [dragRatio]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 0.1 : 0.02;
    if (e.key === "ArrowLeft") { e.preventDefault(); setRatio(clamp(ratio - step)); }
    else if (e.key === "ArrowRight") { e.preventDefault(); setRatio(clamp(ratio + step)); }
    else if (e.key === "Home") { e.preventDefault(); setRatio(MIN_RATIO); }
    else if (e.key === "End") { e.preventDefault(); setRatio(MAX_RATIO); }
    else if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setRatio(0.5); }
  };

  if (!active) return <>{children}</>;

  return (
    <div ref={containerRef} className="flex h-full min-h-0 w-full print:block">
      <div
        className="min-w-0 overflow-auto print:!overflow-visible print:!w-full"
        style={{ flexBasis: `${shown * 100}%`, flexGrow: 0, flexShrink: 0 }}
      >
        {children}
      </div>

      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize the panes"
        aria-valuenow={Math.round(shown * 100)}
        aria-valuemin={Math.round(MIN_RATIO * 100)}
        aria-valuemax={Math.round(MAX_RATIO * 100)}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={onKeyDown}
        onDoubleClick={() => setRatio(0.5)}
        title="Drag to resize — double-click to even them up"
        className={`group relative w-1.5 shrink-0 cursor-col-resize bg-line transition-colors hover:bg-qw-teal focus:bg-qw-teal focus:outline-none print:hidden ${
          dragRatio !== null ? "bg-qw-teal" : ""
        }`}
      >
        {/* A wider invisible hit area than the 6px the eye needs. */}
        <span className="absolute inset-y-0 -left-1 -right-1" />
      </div>

      <div className="min-w-0 flex-1 print:hidden">
        <SecondaryPane />
      </div>
    </div>
  );
};

export default SplitWorkspace;
